import React from 'react';
import { useState } from 'react';
import { useAlert } from '../../../providers/AlertProvider';
import EditPONumberModal from '../EditPONumberModal';
import { update_purchase_order } from '../../../api/purchase_orders';
import { downloadCSV } from '../../lib/functions';
import Button from '../Button';

const PurchaseOrdersReport = ({ reportData, theme }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [editPOData, setEditPOData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const { showAlert } = useAlert();

    const handleRowClick = (po) => {
        setEditPOData(po);
        setShowEditModal(true);
    };

    const handleEditSave = async (updatedPO) => {
        setShowEditModal(false);
        setEditPOData(null);
        setIsLoading(true);
        try {
            const { data, status } = await update_purchase_order(updatedPO);
            if (status === 200 && data.success) {
                showAlert({
                    message: data.message || 'PO updated successfully',
                    type: 'success',
                    buttons: [{ type: 'success', label: 'Close' }],
                });
                // Optionally refresh report data here if you have a fetch function
            } else {
                showAlert({
                    message: data.error || 'Failed to update PO',
                    type: 'danger',
                    buttons: [{ type: 'danger', label: 'Close' }],
                });
            }
        } catch (error) {
            showAlert({
                message: 'An error occurred while updating the PO',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = () => {
        if (!reportData || reportData.length === 0) {
            showAlert({
                message: 'No data available for download.',
                type: 'warning',
                buttons: [{ type: 'warning', label: 'Close' }],
            });
            return;
        }

        const csvData = reportData.map(po => {
            const dateObj = new Date(po.date);
            const formattedDate = isNaN(dateObj)
                ? po.date
                : `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`;
            
            return {
                'Date': formattedDate,
                'PO Number': po.po_number || '',
                'Category': po.category || '',
                'Supplier': po.supplier || '',
                'Comment': po.comment || '',
                'Cost': `£${parseFloat(po.cost || 0).toFixed(2)}`
            };
        });

        downloadCSV(csvData);
        
        showAlert({
            message: 'CSV file downloaded successfully!',
            type: 'success',
            buttons: [{ type: 'success', label: 'OK' }],
        });
    };

    const isLightTheme = theme === 'light';

    if (!reportData || reportData.length === 0) {
        return (
            <div style={{
                padding: '20px',
                textAlign: 'center',
                color: isLightTheme ? '#666' : '#ccc',
                backgroundColor: isLightTheme ? '#f5f5f5' : '#2d2d2d',
                borderRadius: '10px',
                border: `1px solid ${isLightTheme ? '#ddd' : '#555'}`,
                margin: '20px 0'
            }}>
                <p>No purchase orders found.</p>
            </div>
        );
    }

    // Calculate total cost
    const totalCost = reportData.reduce((sum, po) => sum + parseFloat(po.cost || 0), 0);

    return (
        <div>
            {/* Summary Stats */}
            <div style={{
                display: 'flex',
                gap: '20px',
                marginBottom: '20px',
                flexWrap: 'wrap'
            }}>
                <div style={{
                    background: isLightTheme ? '#fff' : '#333',
                    padding: '15px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    border: `1px solid ${isLightTheme ? '#e0e0e0' : '#555'}`,
                    textAlign: 'center',
                    flex: 1
                }}>
                    <h4 style={{ color: '#28a745', margin: '0 0 5px 0' }}>{reportData.length}</h4>
                    <p style={{ color: isLightTheme ? '#666' : '#ccc', margin: '0', fontSize: '14px' }}>Total Orders</p>
                </div>
                <div style={{
                    background: isLightTheme ? '#fff' : '#333',
                    padding: '15px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    border: `1px solid ${isLightTheme ? '#e0e0e0' : '#555'}`,
                    textAlign: 'center',
                    flex: 1
                }}>
                    <h4 style={{ color: '#007bff', margin: '0 0 5px 0' }}>£{totalCost.toFixed(2)}</h4>
                    <p style={{ color: isLightTheme ? '#666' : '#ccc', margin: '0', fontSize: '14px' }}>Total Value</p>
                </div>
            </div>

            {/* Download Button */}
            <div style={{ marginBottom: '20px', textAlign: 'right' }}>  
                <Button
                    color="success"
                    onClick={handleDownload}
                >
                    Download CSV
                </Button>
            </div>

            {/* Table */}
            <div style={{ 
                maxHeight: '70vh', 
                overflowY: 'auto',
                border: `1px solid ${isLightTheme ? '#ddd' : '#555'}`,
                borderRadius: '8px'
            }}>
                <table className={`table table-striped table-hover mb-0 ${isLightTheme ? 'table-light' : 'table-dark'}`} style={{ 
                    width: '100%', 
                    borderCollapse: 'collapse'
                }}>
                    <thead className={isLightTheme ? 'table-dark' : 'table-light'}>
                        <tr style={{ 
                            position: 'sticky',
                            top: 0,
                            zIndex: 1
                        }}>
                            <th
                                className="text-center"
                                style={{
                                    padding: '12px 8px',
                                    fontWeight: 'bold'
                                }}
                            >
                                Date
                            </th>
                            <th
                                className="text-center"
                                style={{
                                    padding: '12px 8px',
                                    fontWeight: 'bold'
                                }}
                            >
                                PO Number
                            </th>
                            <th
                                className="text-center"
                                style={{
                                    padding: '12px 8px',
                                    fontWeight: 'bold'
                                }}
                            >
                                Supplier
                            </th>
                            <th
                                className="text-center"
                                style={{
                                    padding: '12px 8px',
                                    fontWeight: 'bold'
                                }}
                            >
                                Category
                            </th>
                            <th
                                className="text-center"
                                style={{
                                    padding: '12px 8px',
                                    fontWeight: 'bold'
                                }}
                            >
                                Comment
                            </th>
                            <th
                                className="text-center"
                                style={{
                                    padding: '12px 8px',
                                    fontWeight: 'bold'
                                }}
                            >
                                Cost
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.map((po, index) => {
                            // Format date as DD-MM-YYYY to match Fire Register
                            const dateObj = new Date(po.date);
                            const formattedDate = isNaN(dateObj)
                                ? po.date
                                : `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`;
                            return (
                                <tr key={index} style={{ cursor: 'pointer' }} onClick={() => handleRowClick(po)}>
                                    <td className="text-center" style={{ padding: '10px 8px' }}>{formattedDate}</td>
                                    <td className="text-center" style={{ padding: '10px 8px' }}>{po.po_number}</td>
                                    <td className="text-center" style={{ padding: '10px 8px' }}>{po.supplier || '-'}</td>
                                    <td className="text-center" style={{ padding: '10px 8px' }}>{po.category || '-'}</td>
                                    <td className="text-center" style={{ padding: '10px 8px' }}>{po.comment}</td>
                                    <td className="text-center" style={{ padding: '10px 8px' }}>£{parseFloat(po.cost || 0).toFixed(2)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {/* Edit Modal */}
            <EditPONumberModal
                show={showEditModal}
                onClose={() => { setShowEditModal(false); setEditPOData(null); }}
                poData={editPOData}
                onSave={handleEditSave}
                theme={theme}
            />
        </div>
    );
};

export default PurchaseOrdersReport;
